import React, { Dispatch, SetStateAction, useState } from 'react'
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  Autocomplete,
  TextField,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { EligibleStaffResponse } from '@/core/models/planning.model'
import { filterStaffOptions } from '../lib/staffSearch'
import { useLingui } from '@lingui/react'
import { t, Trans } from '@lingui/macro'
import { LoadingButton } from '@mui/lab'

type StaffSelectorProps = {
  alreadyAssignedStaffIds?: string[]
  eligibleStaffs: EligibleStaffResponse[]
  isLoading?: boolean
  isMultipleStaffRequired?: boolean
  onStaffSelected: (staffIds: string[]) => void
  requiredStaffCount: number
  selectedStaffIds: string[]
}

export function StaffSelector({
  alreadyAssignedStaffIds = [],
  eligibleStaffs,
  isLoading = false,
  isMultipleStaffRequired,
  onStaffSelected,
  requiredStaffCount,
  selectedStaffIds,
}: StaffSelectorProps) {
  useLingui()
  const [selectedStaffId, setSelectedStaffId] = useState<string>('')

  // Filter out already assigned staff
  const availableStaffs = eligibleStaffs.filter((staff) => !alreadyAssignedStaffIds.includes(staff._id))

  const canAddMoreStaff = selectedStaffIds.length < requiredStaffCount
  const allStaffSelected = selectedStaffIds.length === requiredStaffCount

  const handleAddStaff = () => {
    if (selectedStaffId && !selectedStaffIds.includes(selectedStaffId) && canAddMoreStaff) {
      const newSelectedStaffIds = [...selectedStaffIds, selectedStaffId]
      onStaffSelected(newSelectedStaffIds)
      setSelectedStaffId('') // Clear selection after adding
    }
  }

  const handleRemoveStaff = (staffIdToRemove: string) => {
    const newSelectedStaffIds = selectedStaffIds.filter((id) => id !== staffIdToRemove)
    onStaffSelected(newSelectedStaffIds)
  }

  // Get full staff objects for the selected IDs
  const selectedStaffList = selectedStaffIds.map((id) => eligibleStaffs.find((staff) => staff._id === id)).filter(Boolean) as EligibleStaffResponse[]

  if (availableStaffs.length === 0) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
        <Trans>No eligible staff</Trans>
      </Typography>
    )
  }

  return (
    <Stack spacing={2}>
      {isMultipleStaffRequired && (
        <Alert severity="info" icon={<></>}>
          <Typography variant="body2" fontWeight="medium">
            <Trans>Multiple staff required</Trans>
          </Typography>
          <Typography variant="caption">
            <Trans>Multiple staff are required for this shift. Please assign staff members to the shift from the staff list below.</Trans>
          </Typography>
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          <Trans>Staff assigned</Trans>
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(selectedStaffIds.length / requiredStaffCount) * 100}
          color={allStaffSelected ? 'success' : 'primary'}
          sx={{
            height: 8,
            borderRadius: 4,
            '& .MuiLinearProgress-bar': {
              transition: 'transform 0.5s ease',
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            textAlign: 'right',
            fontWeight: allStaffSelected ? 'bold' : 'normal',
            color: allStaffSelected ? 'success.main' : 'text.secondary',
          }}
        >
          <Trans>
            {selectedStaffIds.length} / {requiredStaffCount} Staff selected
          </Trans>
        </Typography>
      </Box>

      {isMultipleStaffRequired && selectedStaffIds.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            <Trans>Selected staffs</Trans>
          </Typography>
          <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            {selectedStaffList.map((staff) => (
              <ListItem
                key={staff._id}
                secondaryAction={
                  <IconButton edge="end" onClick={() => handleRemoveStaff(staff._id)}>
                    <DeleteIcon />
                  </IconButton>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: staff.isAvailable ? 'primary.main' : 'warning.main',
                      width: 32,
                      height: 32,
                    }}
                  >
                    {staff.userInfo.name.charAt(0)}
                    {staff.userInfo.surname.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">
                      {staff.userInfo.name} {staff.userInfo.surname}
                    </Typography>
                    {staff.speciality && (
                      <Typography variant="caption" color="text.secondary">
                        {staff.speciality}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {!canAddMoreStaff && (
        <Alert severity="warning">
          <Typography variant="caption" color="text.secondary">
            <Trans>Maximum staff reached</Trans>
          </Typography>
        </Alert>
      )}

      <StaffSelectorForm
        availableStaffs={availableStaffs}
        selectedStaffIds={selectedStaffIds}
        selectedStaffId={selectedStaffId}
        setSelectedStaffId={setSelectedStaffId}
      />

      {selectedStaffId && canAddMoreStaff && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'primary.50',
            borderRadius: 1,
            borderColor: 'primary.main',
          }}
        >
          <LoadingButton loading={isLoading} variant="contained" startIcon={<AddIcon />} onClick={handleAddStaff} sx={{ flexGrow: 1 }} color="primary">
            <Trans>Add staff</Trans>
          </LoadingButton>
        </Box>
      )}
    </Stack>
  )
}

function StaffSelectorForm({
  availableStaffs,
  selectedStaffIds,
  selectedStaffId,
  setSelectedStaffId,
}: {
  availableStaffs: EligibleStaffResponse[]
  selectedStaffIds: string[]
  selectedStaffId: string
  setSelectedStaffId: Dispatch<SetStateAction<string>>
}) {
  useLingui()

  return (
    <Autocomplete
      fullWidth
      options={availableStaffs}
      value={availableStaffs.find((staff) => staff._id === selectedStaffId) ?? null}
      onChange={(_event, option) => setSelectedStaffId(option?._id ?? '')}
      isOptionEqualToValue={(option, value) => option._id === value?._id}
      getOptionDisabled={(option) => selectedStaffIds.includes(option._id)}
      getOptionLabel={(staff) => `${staff.userInfo.name} ${staff.userInfo.surname}`}
      filterOptions={filterStaffOptions}
      noOptionsText={t`No staff found matching your search`}
      renderInput={(params) => <TextField {...params} label={t`Select staff`} placeholder={t`Search by name or speciality`} />}
      renderOption={(props, staff) => {
        const { key, ...optionProps } = props as typeof props & { key?: string }
        return (
          <Box
            component="li"
            key={key ?? staff._id}
            {...optionProps}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <Avatar sx={{ bgcolor: staff.isAvailable ? 'primary.main' : 'warning.main', width: 36, height: 36 }}>
                {staff.userInfo.name.charAt(0)}
                {staff.userInfo.surname.charAt(0)}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
                  {staff.userInfo.name} {staff.userInfo.surname}
                </Typography>
                {staff.speciality && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {staff.speciality}
                  </Typography>
                )}
                {staff.establishment?.name && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {staff.establishment.name}
                  </Typography>
                )}
              </Box>
            </Box>
            {!staff.isAvailable && <Chip size="small" color="warning" label={t`Unavailable`} sx={{ ml: 2, flexShrink: 0 }} />}
          </Box>
        )
      }}
    />
  )
}
